import React from "react";
import { IGetMyServiceListRes } from "src/service/types/console.type";
import { IGetSiteNxacListRes } from "src/service/types/cloud.type";
import { calcStorageProgressPct, getServiceUrl } from "../utils";
import { useProductFlags } from "src/hooks/recoil/useProductFlags";
import { useRecoilState } from "recoil";
import { productKeyAtom } from "src/atoms";
import { useTranslation } from "react-i18next";

interface Props {
  current: IGetMyServiceListRes;
  siteNxacList: IGetSiteNxacListRes[];
  userLastLogin: string;
  dateFormatUtc: (n: string) => string;
  onOpenUserPage: () => void;
  onOpenAdminPage?: () => void;
  canDelete: boolean;
  onClickDelete: () => void;
  calcStorageProgressPct?: typeof calcStorageProgressPct; // 커스텀 계산기 주입 가능
}

const ServiceDetail = ({
  current,
  siteNxacList,
  userLastLogin,
  dateFormatUtc,
  onOpenUserPage,
  onOpenAdminPage,
  canDelete,
  onClickDelete,
}: Props) => {
  const { t } = useTranslation();
  const titleColor = "#28292B";
  const { isOciProduct, isDspProduct, isDspmProduct, isFiresideProduct, isMindSatProduct } = useProductFlags();
  const [productKey] = useRecoilState(productKeyAtom);
  // 사이트 주소는 productKey/isOciProduct/도메인으로 내부 계산 (부모 주입 불필요)
  const currentUrl = getServiceUrl(productKey, isOciProduct, current?.domainName ?? "");
  const storagePct = calcStorageProgressPct(
    current.bucketSizeMB,
    current.licenseCount,
    current.userPerStorage
  );

  return (
    <div className="serviceMng">
      <div className="serviceMng__header flex">
        <div className="serviceMng-thumnail">
          {current?.serviceName?.substring(0, 2)}
        </div>
        <div className="serviceMng-name">
          <h4 className="serviceMng-name-title">{current?.serviceName}</h4>
          <h4 className="serviceMng-name-url">{currentUrl}</h4>
        </div>
        <div className="serviceMng__btn mg ml-auto">
          <button
            type="button"
            className="btn btn-gray"
            onClick={onOpenUserPage}
          >
            {t("siteManagement.createSite.goUserPageBtn")}
          </button>

          {!isOciProduct &&
            productKey === "WRAPSODY_ECO" &&
            !!onOpenAdminPage && (
              <button
                type="button"
                className="serviceMng-btn btn btn-green mg ml-10"
                onClick={onOpenAdminPage}
              >
                {t("siteManagement.createSite.goAdminPageBtn")}
              </button>
            )}
        </div>
      </div>

      <div className="serviceDetail">
        <div className="flex justify-space-between align-center">
          <b
            className="text text--lg text--blackLt"
            style={{ marginBottom: 10, color: titleColor }}
          >
            {t("siteManagement.createSite.detail.title")}
          </b>

          {canDelete && (
            <button
              type="button"
              className="btn btn-transparent text text--orange"
              onClick={onClickDelete}
            >
              <i className="icon_trash inline-block vertical-middle" />
              <span className="inline-block vertical-middle mg ml-2">
                {t("siteManagement.createSite.delete")}
              </span>
            </button>
          )}
        </div>

        <hr className="mg mt-0" />

        <div className="serviceDetail__item">
          <div className="serviceDetail__itemKey">
            {t("siteManagement.createSite.detail.siteName")}
          </div>
          <div className="serviceDetail__itemValue">{current.serviceName}</div>
        </div>

        <div className="serviceDetail__item">
          <div className="serviceDetail__itemKey">
            {t("siteManagement.createSite.detail.siteAddress")}
          </div>
          <div className="serviceDetail__itemValue">{currentUrl}</div>
        </div>

        <hr />

        <div className="serviceDetail__item">
          <div className="serviceDetail__itemKey">
            {t("siteManagement.createSite.detail.maxUser")}
          </div>
          <div className="serviceDetail__itemValue">{current.licenseCount}</div>
        </div>

        <div className="serviceDetail__item">
          <div className="serviceDetail__itemKey">
            {t("siteManagement.createSite.detail.createDate")}
          </div>
          <div className="serviceDetail__itemValue">
            {current?.serviceEntryTime
              ? dateFormatUtc(current.serviceEntryTime)
              : "-"}
          </div>
        </div>

        <hr />

        <div className="serviceDetail__item">
          <div className="serviceDetail__itemKey">
            {t("siteManagement.createSite.detail.lastAccessDate")}
          </div>
          <div className="serviceDetail__itemValue">{userLastLogin}</div>
        </div>

        {productKey === "WRAPSODY_ECO" &&
          current?.priceType === "plus" &&
          siteNxacList?.length > 0 && (
            <>
              <hr />
              <div className="serviceDetail__item">
                <div className="serviceDetail__itemKey">
                  {t("admin.upc.site.applicationModal")}
                </div>
                <div className="serviceDetail__itemValue">
                  {siteNxacList.map((nxac, idx) => (
                    <span key={nxac?.nxacAppUid ?? idx}>
                      {nxac?.applicationName}
                      {idx !== siteNxacList.length - 1 && ", "}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

        {!isOciProduct && (
          <div className="serviceDetail__item">
            <div className="serviceDetail__itemKey">
              {t("siteManagement.createSite.detail.serverLocation")}
            </div>
            <div className="serviceDetail__itemValue">
              {current.region === "us-east-1"
                ? t("siteManagement.createSiteModal.serverLocation.option_2")
                : t("siteManagement.createSiteModal.serverLocation.option_1")}
            </div>
          </div>
        )}

        {!isOciProduct &&
          !isDspProduct &&
          !isFiresideProduct &&
          !isMindSatProduct &&
          !isDspmProduct && (
            // 용량
            <>
              <hr />
              <div className="serviceDetail__item">
                <div className="serviceDetail__itemKey">
                  {t("siteManagement.createSite.detail.storage.fieldname")}
                </div>

                <div className="serviceDetail__itemValue">
                  <div className="flex">
                    <div className="progress progress__chart mg mb-4">
                      <div
                        className="progress-bar"
                        style={{ width: `${storagePct}%` }}
                      />
                    </div>
                    <div className="progress__info mg ml-10">
                      <b>
                        {(
                          Number(current.bucketSizeMB) /
                          1024 /
                          1024 /
                          1024
                        ).toLocaleString()}
                      </b>
                      {t("siteManagement.createSite.detail.storage.byteUnit")}
                      &nbsp;/&nbsp;
                      {Number(current.userPerStorage) === 0 ? (
                        t("siteManagement.createSite.detail.storage.unlimit")
                      ) : (
                        <>
                          <b>
                            {(
                              Number(current.licenseCount) *
                              Number(current.userPerStorage)
                            ).toLocaleString()}
                          </b>
                          {t(
                            "siteManagement.createSite.detail.storage.byteUnit"
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text text--gray">
                    {t(
                      `siteManagement.createSite.detail.storage.info${isFiresideProduct ? "_fireside" : ""
                      }`
                    )
                      .split("\n")
                      .map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                  </p>
                </div>
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default ServiceDetail;
